'use client'

import { useState, useEffect } from 'react'
import { useLocale } from 'next-intl'
import { useCurrency } from '@/lib/currency'
import { useAuth } from '@/context/AuthContext'
import { originSlug, destSlug, DURATION_SLUG_LABEL } from '@/lib/dealSlug'

const ALL_DURATIONS = 'weekend,1_week,2_weeks,lh_2_weeks'
const DUR_NIGHT_RANGE = { weekend: [2,4], week: [5,9], '1_week': [5,9], '2-weeks': [10,16], '2_weeks': [10,16], lh_2_weeks: [10,16] }

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const MAX_TRIPS   = 16

function nightsToDurSlug(nights) {
  if (nights <= 4) return 'weekend'
  if (nights <= 9) return 'week'
  return '2-weeks'
}

function assignLanes(trips) {
  const sorted  = [...trips].sort((a, b) => a.departure_date.localeCompare(b.departure_date))
  const laneEnd = []
  return sorted.map(trip => {
    let lane = laneEnd.findIndex(end => end < trip.departure_date)
    if (lane === -1) lane = laneEnd.length
    laneEnd[lane] = trip.return_date
    return { ...trip, lane }
  })
}

function GanttMonth({ year, month, trips, origin, destinations, fmt, locale, isLoggedIn, featuredRange }) {
  const daysInMonth = new Date(year, month, 0).getDate()
  const destination = Array.isArray(destinations) ? destinations[0] : destinations

  const label      = `${MONTH_NAMES[month - 1]} ${year}`
  const dealPrices = trips.filter(t => t.is_deal).map(t => t.price)
  const minPrice   = dealPrices.length ? Math.min(...dealPrices) : null
  const laned      = assignLanes(trips)
  const numLanes   = laned.length ? Math.max(...laned.map(t => t.lane)) + 1 : 0

  const leftPct  = day => ((day - 1) / daysInMonth) * 100
  const rightPct = day => ((daysInMonth - day) / daysInMonth) * 100

  if (dealPrices.length === 0) return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-gray-800">{label}</span>
      </div>
      <div className="h-8 flex items-center">
        <span className="text-xs text-gray-500">No notable flights found for this period yet</span>
      </div>
    </div>
  )

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-gray-800">{label}</span>
        {minPrice && <span className="text-xs text-gray-500">from {fmt(minPrice).primary}</span>}
      </div>

      <div className="relative h-4 mb-1 select-none">
        {Array.from({ length: daysInMonth }, (_, i) => {
          const d   = i + 1
          const dow = (new Date(year, month - 1, d).getDay() + 6) % 7
          return (
            <span
              key={d}
              className={`absolute text-[10px] leading-none ${dow >= 5 ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}
              style={{ left: `${((d - 0.5) / daysInMonth) * 100}%`, transform: 'translateX(-50%)' }}
            >
              {d}
            </span>
          )
        })}
      </div>

      <div className="space-y-1">
        {Array.from({ length: numLanes }, (_, laneIdx) => {
          const laneTrips = laned.filter(t => t.lane === laneIdx)
          return (
            <div key={laneIdx} className="relative h-7">
              <div className="absolute inset-0 flex pointer-events-none" aria-hidden>
                {Array.from({ length: daysInMonth }, (_, j) => (
                  <div key={j} className="flex-1 border-r border-gray-100 last:border-0" />
                ))}
              </div>

              {laneTrips.map((trip, i) => {
                const depDay       = parseInt(trip.departure_date.slice(8), 10)
                const retMonthStr  = trip.return_date.slice(0, 7)
                const thisMonthStr = `${year}-${String(month).padStart(2, '0')}`
                const retDay = retMonthStr === thisMonthStr
                  ? parseInt(trip.return_date.slice(8), 10)
                  : daysInMonth
                const nights = Math.round(
                  (new Date(trip.return_date + 'T00:00:00') - new Date(trip.departure_date + 'T00:00:00')) / 86400000
                )
                const isFeatured = !featuredRange || (nights >= featuredRange[0] && nights <= featuredRange[1])
                const isCheapestDeal = trip.is_deal && minPrice !== null && trip.price === minPrice
                const barClass = trip.is_deal
                  ? isFeatured
                    ? isCheapestDeal
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  : 'bg-gray-100 text-gray-600 cursor-default'

                const internalHref = trip.is_deal
                  ? `/deals/${originSlug(origin)}-${destSlug(destination)}-${nightsToDurSlug(nights)}`
                  : null
                const href = !isLoggedIn ? '/signin' : internalHref
                const Tag = href ? 'a' : 'div'

                return (
                  <Tag
                    key={i}
                    href={href || undefined}
                    title={`${trip.departure_date} → ${trip.return_date} · ${fmt(trip.price).primary} · ${nights} nights${trip.is_deal ? ' 🔥' : ''}`}
                    className={`absolute inset-y-1 rounded flex items-center overflow-hidden transition-colors ${barClass} ${href ? 'cursor-pointer' : ''}`}
                    style={{ left: `${leftPct(depDay)}%`, right: `${rightPct(retDay)}%`, minWidth: '4px' }}
                  >
                    <span className="absolute left-0 inset-y-0 w-1 bg-black/15 rounded-l pointer-events-none" />
                    <span className="absolute right-0 inset-y-0 w-1 bg-black/15 rounded-r pointer-events-none" />
                    <span className="truncate px-2 text-xs font-medium leading-none">{fmt(trip.price).primary} · {nights}n</span>
                  </Tag>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function FlightsCalendarClient({ origin, destinations, durations, durationSlug }) {
  const locale = useLocale()
  const { formatDealPrice } = useCurrency()
  const { currentUser } = useAuth()
  const isLoggedIn = !!currentUser
  const featuredRange = durationSlug ? (DUR_NIGHT_RANGE[durationSlug] || null) : null
  const durationLabel = durationSlug ? (DURATION_SLUG_LABEL[durationSlug] || durationSlug) : null
  const [allPrices, setAllPrices] = useState(null)
  const [loading,   setLoading]   = useState(true)

  useEffect(() => {
    setLoading(true)
    const destParam = Array.isArray(destinations) ? destinations.join(',') : destinations
    const durParam = durations ? (Array.isArray(durations) ? durations.join(',') : durations) : ALL_DURATIONS
    fetch(`/api/calendar-prices?origin=${origin}&destination=${encodeURIComponent(destParam)}&duration=${encodeURIComponent(durParam)}`)
      .then(r => r.json())
      .then(d => { setAllPrices(d.prices || []); setLoading(false) })
      .catch(() => { setAllPrices([]); setLoading(false) })
  }, [origin, Array.isArray(destinations) ? destinations.join(',') : destinations])

  const months = []
  const now = new Date()
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1)
    months.push({ year: d.getFullYear(), month: d.getMonth() + 1 })
  }

  function getMonthTrips(year, month) {
    if (!allPrices) return []
    const prefix = `${year}-${String(month).padStart(2, '0')}-`
    // Cheapest per departure_date+return_date combo
    const best = {}
    for (const e of allPrices) {
      if (!e.departure_date?.startsWith(prefix) || !e.return_date) continue
      const k = `${e.departure_date}-${e.return_date}`
      if (!best[k] || e.price < best[k].price) best[k] = e
    }
    return Object.values(best)
      .sort((a, b) => a.price - b.price)
      .slice(0, MAX_TRIPS)
  }

  return (
    <div>
      {loading ? (
        <div className="text-center py-16 text-gray-500 text-sm animate-pulse">Loading prices…</div>
      ) : !allPrices?.length ? (
        <div className="py-12 text-center space-y-2">
          <p className="text-gray-500 text-sm">No price data yet for this route.</p>
          <p className="text-gray-500 text-xs">We scan prices hourly — check back soon.</p>
        </div>
      ) : (
        <div className="flex flex-col">
          {months.map(({ year, month }) => (
            <GanttMonth
              key={`${year}-${month}`}
              year={year}
              month={month}
              trips={getMonthTrips(year, month)}
              origin={origin}
              destinations={destinations}
              fmt={formatDealPrice}
              locale={locale}
              isLoggedIn={isLoggedIn}
              featuredRange={featuredRange}
            />
          ))}
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-500">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-6 h-3 rounded bg-blue-600" />
          <span>{durationLabel ? `${durationLabel} deals` : 'Deal'}</span>
        </div>
        {durationLabel && (
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-6 h-3 rounded bg-gray-300" />
            <span>Other deals</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-6 h-3 rounded bg-gray-200" />
          <span>Reference flight</span>
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-500">Deal bars link to deal details · prices updated hourly</p>
    </div>
  )
}
